import { useState, useEffect } from 'react';
import { Plus, Edit2, Loader2, X, UserCheck, UserX } from 'lucide-react';
import { authApi } from '@/api/client';
import type { User } from '@/types';
import { format, parseISO } from 'date-fns';
import { ja } from 'date-fns/locale';
import clsx from 'clsx';

export function UsersPage() {
  const [users, setUsers] = useState<User[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingUser, setEditingUser] = useState<User | null>(null);
  const [error, setError] = useState<string | null>(null);

  const fetchUsers = async () => {
    setIsLoading(true);
    const response = await authApi.getUsers();
    if (response.success && response.data) {
      setUsers(response.data);
    } else {
      setError(response.error || 'ユーザー一覧の取得に失敗しました');
    }
    setIsLoading(false);
  };

  useEffect(() => {
    fetchUsers();
  }, []);

  const openEditModal = (user: User) => {
    setEditingUser(user);
    setShowModal(true);
  };

  const openCreateModal = () => {
    setEditingUser(null);
    setShowModal(true);
  };

  const toggleUserStatus = async (user: User) => {
    const newStatus = !user.is_active;
    const action = newStatus ? '有効化' : '無効化';
    
    if (!confirm(`「${user.name}」を${action}しますか？`)) return;

    const response = await authApi.updateUser(user.id, { is_active: newStatus });
    if (response.success) {
      fetchUsers();
    } else {
      alert(response.error || `${action}に失敗しました`);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-gray-900">ユーザー管理</h1>
        <button onClick={openCreateModal} className="btn-primary flex items-center gap-2">
          <Plus className="h-4 w-4" />
          ユーザー追加
        </button>
      </div>

      {error && (
        <div className="bg-red-50 text-red-700 p-4 rounded-lg">
          {error}
        </div>
      )}

      {/* Users Table */}
      <div className="card">
        {isLoading ? (
          <div className="flex items-center justify-center h-48">
            <Loader2 className="h-8 w-8 animate-spin text-primary-600" />
          </div>
        ) : users.length === 0 ? (
          <div className="text-center py-12 text-gray-500">
            ユーザーがいません
          </div>
        ) : (
          <div className="table-container">
            <table className="table">
              <thead>
                <tr>
                  <th>名前</th>
                  <th>メールアドレス</th>
                  <th>権限</th>
                  <th>状態</th>
                  <th>登録日</th>
                  <th className="text-right">操作</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {users.map((user) => (
                  <tr key={user.id} className={clsx(!user.is_active && 'bg-gray-50')}>
                    <td className="font-medium">{user.name}</td>
                    <td>{user.email}</td>
                    <td>
                      <span className={clsx(
                        'badge',
                        user.role === 'admin' ? 'badge-info' : 'bg-gray-100 text-gray-700'
                      )}>
                        {user.role === 'admin' ? '管理者' : '一般'}
                      </span>
                    </td>
                    <td>
                      <span className={clsx(
                        'badge',
                        user.is_active ? 'badge-success' : 'badge-danger'
                      )}>
                        {user.is_active ? '有効' : '無効'}
                      </span>
                    </td>
                    <td>
                      {format(parseISO(user.created_at), 'yyyy/MM/dd', { locale: ja })}
                    </td>
                    <td className="text-right">
                      <div className="flex items-center justify-end gap-2">
                        <button
                          onClick={() => openEditModal(user)}
                          className="p-1 text-gray-500 hover:text-primary-600"
                          title="編集"
                        >
                          <Edit2 className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => toggleUserStatus(user)}
                          className={clsx(
                            'p-1',
                            user.is_active
                              ? 'text-gray-500 hover:text-red-600'
                              : 'text-gray-500 hover:text-green-600'
                          )}
                          title={user.is_active ? '無効化' : '有効化'}
                        >
                          {user.is_active ? (
                            <UserX className="h-4 w-4" />
                          ) : (
                            <UserCheck className="h-4 w-4" />
                          )}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Create/Edit Modal */}
      {showModal && (
        <UserModal
          user={editingUser}
          onClose={() => setShowModal(false)}
          onSave={() => {
            setShowModal(false);
            fetchUsers();
          }}
        />
      )}
    </div>
  );
}

interface UserModalProps {
  user: User | null;
  onClose: () => void;
  onSave: () => void;
}

function UserModal({ user, onClose, onSave }: UserModalProps) {
  const [form, setForm] = useState({
    name: user?.name || '',
    email: user?.email || '',
    password: '',
    role: user?.role || 'user' as 'admin' | 'user',
  });
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError(null);

    let response;
    if (user) {
      // Update existing user
      const updateData: Record<string, string | boolean> = {
        name: form.name,
        role: form.role,
      };
      if (form.password) {
        updateData.password = form.password;
      }
      response = await authApi.updateUser(user.id, updateData);
    } else {
      // Create new user
      if (!form.password) {
        setError('パスワードを入力してください');
        setIsLoading(false);
        return;
      }
      response = await authApi.createUser({
        email: form.email,
        password: form.password,
        name: form.name,
        role: form.role,
      });
    }

    if (response.success) {
      onSave();
    } else {
      setError(response.error || '保存に失敗しました');
    }
    setIsLoading(false);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="fixed inset-0 bg-black/50" onClick={onClose} />
      <div className="relative bg-white rounded-lg shadow-xl w-full max-w-md mx-4">
        <div className="flex items-center justify-between p-4 border-b">
          <h2 className="text-lg font-semibold">
            {user ? 'ユーザー編集' : 'ユーザー追加'}
          </h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X className="h-5 w-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-4 space-y-4">
          {error && (
            <div className="bg-red-50 text-red-700 p-3 rounded-lg text-sm">
              {error}
            </div>
          )}

          <div>
            <label className="label">名前 *</label>
            <input
              type="text"
              required
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              className="input"
              placeholder="山田 太郎"
            />
          </div>

          <div>
            <label className="label">メールアドレス *</label>
            <input
              type="email"
              required
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              className="input"
              placeholder="example@example.com"
              disabled={!!user}
            />
            {user && (
              <p className="text-xs text-gray-500 mt-1">
                メールアドレスは変更できません
              </p>
            )}
          </div>

          <div>
            <label className="label">
              パスワード {user ? '（変更する場合のみ）' : '*'}
            </label>
            <input
              type="password"
              value={form.password}
              onChange={(e) => setForm({ ...form, password: e.target.value })}
              className="input"
              placeholder={user ? '変更しない場合は空欄' : '8文字以上'}
              minLength={user ? 0 : 8}
              required={!user}
            />
          </div>

          <div>
            <label className="label">権限 *</label>
            <select
              value={form.role}
              onChange={(e) => setForm({ ...form, role: e.target.value as 'admin' | 'user' })}
              className="input"
            >
              <option value="user">一般ユーザー</option>
              <option value="admin">管理者</option>
            </select>
          </div>

          <div className="flex gap-3 pt-4">
            <button type="button" onClick={onClose} className="btn-secondary flex-1">
              キャンセル
            </button>
            <button type="submit" disabled={isLoading} className="btn-primary flex-1">
              {isLoading ? (
                <Loader2 className="h-4 w-4 animate-spin mx-auto" />
              ) : (
                user ? '更新' : '追加'
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
